import { useEffect, useRef } from 'react';
import { useMutation, useQuery } from 'react-query';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';

const { REACT_APP_API_HOST } = process.env;

/**
 * Route the approval emails link to:
 * `/timeaway/requests/<id>?action=approve|decline` (see the
 * ABSENCE_REQUEST_APPROVAL email in the API). Without `action` the page only
 * shows the request.
 */
export const EMAILED_DECISION_ROUTE = '/timeaway/requests/:id';

/** Older in-app route, `/timeaway/<id>/<decision>`. */
export const LEGACY_DECISION_ROUTE = '/timeaway/:id/:decision';

type ApiDecision = 'approve' | 'reject';

/**
 * First path segments under /timeaway/ that are pages, not request ids.
 * `/timeaway/documents/<id>` used to fall through to the legacy decision
 * route (id "documents", decision "<id>"); those links now redirect
 * elsewhere, and this makes sure such a path can never record a decision.
 */
const NOT_REQUEST_IDS = new Set([
  'documents',
  'my-requests',
  'my-allowance',
  'requests',
]);

// The emails say "decline" but the API's decision endpoint only accepts
// `approve`, `reject` and `request_changes`.
const API_DECISIONS: Record<string, ApiDecision> = {
  approve: 'approve',
  decline: 'reject',
  reject: 'reject',
};

type TimeawayDetails = {
  applicant?: string;
  date_submitted?: string;
  start_date?: string;
  end_date?: string;
  days_requested?: string;
  type?: string;
  status?: string;
  decision_by?: string;
  date_approved?: string;
};

function errorMessage(error: unknown): string {
  const message = (error as any)?.response?.data?.message;
  return typeof message === 'string' && message
    ? message
    : 'Something went wrong. Please try again later.';
}

export const DecisionConfirmationRoute = () => {
  const { id, decision } = useParams();
  const [searchParams] = useSearchParams();
  const requested = (
    decision ??
    searchParams.get('action') ??
    ''
  ).toLowerCase();
  const apiDecision: ApiDecision | undefined =
    id && NOT_REQUEST_IDS.has(id) ? undefined : API_DECISIONS[requested];
  const isUnknownAction = requested !== '' && !apiDecision;

  const {
    mutate,
    data: decisionResponse,
    error: decisionError,
    isIdle,
    isLoading: isDeciding,
    isError: decisionFailed,
    isSuccess: decisionSucceeded,
  } = useMutation(async () => {
    const { data } = await axios.put(
      `${REACT_APP_API_HOST}/v1/timeaway/${id}/${apiDecision}`,
    );
    return data;
  });

  // Recording a decision is not idempotent, so it must run exactly once per
  // link: not again on re-render, window focus, or React strict mode's
  // double-invoked effects. The ref remembers which link already fired. The
  // call is deferred to a timer that cleanup cancels, so under strict mode it
  // fires after the final mount, when useMutation is subscribed again (a
  // mutation started before strict mode's simulated unmount never reports
  // its result back to the component).
  const firedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!id || !apiDecision) return;
    const key = `${id}:${apiDecision}`;
    if (firedFor.current === key) return;
    const timer = setTimeout(() => {
      firedFor.current = key;
      mutate();
    }, 0);
    return () => clearTimeout(timer);
  }, [id, apiDecision, mutate]);

  // Viewing the request without an action only reads it.
  const { data: viewResponse, isError: viewFailed } = useQuery(
    ['timeaway-request', id],
    async () => {
      const { data } = await axios.get(
        `${REACT_APP_API_HOST}/v1/timeaway/${id}`,
      );
      return data;
    },
    { enabled: Boolean(id) && requested === '', retry: false },
  );

  const timeaway: TimeawayDetails | undefined = apiDecision
    ? decisionResponse?.timeaway
    : viewResponse?.timeaway;

  const renderHeading = () => {
    if (isUnknownAction) {
      return {
        title: 'Unknown action',
        message: `"${requested}" is not a valid decision for this request.`,
      };
    }

    if (apiDecision) {
      if (isIdle || isDeciding) {
        return {
          title: 'Recording decision',
          message: 'Please wait while we record your decision.',
        };
      }

      if (decisionFailed) {
        return {
          title: 'Decision not recorded',
          message: errorMessage(decisionError),
        };
      }

      if (decisionSucceeded) {
        return apiDecision === 'approve'
          ? {
              title: 'Request Approved',
              message: 'This time away request has been approved!',
            }
          : {
              title: 'Request Declined',
              message: 'This time away request has been declined.',
            };
      }
    }

    if (viewFailed) {
      return {
        title: 'Request not found',
        message: 'This time away request could not be loaded.',
      };
    }

    return {
      title: 'Time Away Request',
      message: timeaway?.status
        ? `This time away request is ${timeaway.status}.`
        : 'Loading request details...',
    };
  };

  const { title, message } = renderHeading();

  return (
    <div className="overflow-hidden rounded-lg bg-white shadow">
      <div className="px-4 py-5 sm:p-6">
        <div className="container mx-auto p-4">
          <header className="mb-6 flex items-center justify-between">
            <img
              src="/redstone-full-white.png"
              alt="Company Logo"
              className="h-12"
            />
            <nav>
              <ul className="flex space-x-4">
                <li>
                  <Link className="text-blue-500" to="/profile">
                    My Requests
                  </Link>
                </li>
              </ul>
            </nav>
          </header>

          <h1 className="mb-4 text-2xl font-bold">{title}</h1>
          <p className="mb-6" role={decisionFailed ? 'alert' : undefined}>
            {message}
          </p>

          {decisionFailed && (
            <div className="mb-6 flex space-x-4">
              <button
                type="button"
                className="rounded bg-blue-500 px-4 py-2 text-white"
                onClick={() => mutate()}
              >
                Try again
              </button>
              {id && (
                <Link
                  className="px-4 py-2 text-blue-500"
                  to={`/time-off/${id}`}
                >
                  Review request
                </Link>
              )}
            </div>
          )}

          <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <h2 className="mb-2 text-xl font-bold">Request Details</h2>
              <p>
                <strong>Employee Name:</strong> {timeaway?.applicant}
              </p>
              <p>
                <strong>Date Submitted:</strong> {timeaway?.date_submitted}
              </p>
              <p>
                <strong>Requested Dates:</strong> {timeaway?.start_date} -{' '}
                {timeaway?.end_date}
              </p>
              <p>
                <strong>Total Days:</strong> {timeaway?.days_requested}
              </p>
              <p>
                <strong>Reason:</strong> {timeaway?.type}
              </p>
            </div>
            <div>
              <h2 className="mb-2 text-xl font-bold">Approval Details</h2>
              <p>
                <strong>Approved by:</strong> {timeaway?.decision_by}
              </p>
              <p>
                <strong>Date Approved:</strong> {timeaway?.date_approved}
              </p>
            </div>
          </div>

          <div className="flex space-x-4">
            <button
              className="rounded bg-blue-500 px-4 py-2 text-white"
              onClick={() => window.print()}
            >
              Print
            </button>
          </div>

          <footer className="mt-12 text-center">
            <p>For support, contact us at support@redstonehr.com.</p>
          </footer>
        </div>
      </div>
    </div>
  );
};
